import { observable, computed, action, runInAction } from 'mobx';
import autobind from 'autobind-decorator';
import { asyncAction } from 'mobx-utils';
import { ScoreService } from 'services';
import SoccerModel from './model';
import { ScoreUtil } from 'utils/scoreUtil';
import WebSocketUtil from 'utils/socketUtil';
import { timeUtils } from 'utils/timeUtil';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { _SPORTS_STATUS } from 'common/constants/sportsConstants';
import AbstractScoreCommon from '../../store/common';

@autobind
class SoccerStore extends AbstractScoreCommon {
  socket = null;
  isSub = false;

  @observable
  list = [];

  @observable
  todayFinalList = [];

  @observable
  suggestList = [
    {
      title: '리그',
      list: []
    },
    {
      title: '팀',
      list: []
    }
  ];

  @observable
  searchValue = {};

  @observable
  loading = false;

  @observable
  todaySelectedGames = [];

  @observable
  selectedTab = 'ALL';

  @observable
  todayFinalCount = 0;

  @observable
  currentDate = timeUtils.todayYmd();

  constructor() {
    super();
  }

  connectSocket(topic) {
    this.socket = new WebSocketUtil(topic);

    this.socket.ws.onmessage = (event) => {
      if (event) {
        let data = event.data;

        try {
          if (data === 'OK') {
            this.isSub = true;
          } else {
            let messageData = JSON.parse(data);

            if (messageData) {
              const { TOPIC, MESSAGE } = messageData;
              const today = timeUtils.todayYmd();

              if (TOPIC.includes('sports')) {
                const data = JSON.parse(MESSAGE);
                const copy = [...this.list.toJS()];
                let index = -1;
                let selectedGameIndex = -1;

                if (data.hasOwnProperty('gameId')) {
                  index = copy.findIndex((item) => item.id === data.gameId);
                  selectedGameIndex = this.selectedList.findIndex(
                    (item) => item.id === data.gameId
                  );
                }

                if (TOPIC === `sports.soccer.games`) {
                  if (
                    this.selectedTab === 'FAVORITE' &&
                    this.currentDate === today
                  ) {
                    if (Boolean(~selectedGameIndex)) {
                      this.todaySelectedGames[selectedGameIndex].updateGame(
                        data.game
                      );
                    }
                  } else {
                    if (Boolean(~index)) {
                      this.list[index].updateGame(data.game);
                    }
                  }
                }

                if (TOPIC === `sports.soccer.period-data`) {
                  const check = this.isFavoriteActiveCheck;

                  if (
                    this.selectedTab === 'FAVORITE' &&
                    this.currentDate === today
                  ) {
                    if (Boolean(~selectedGameIndex)) {
                      this.todaySelectedGames[
                        selectedGameIndex
                      ].updatePeriodData(data.periodData, check, true);
                    }
                  } else {
                    const checkFavorite = this.selectedGames.some(
                      (item) => item.key === data.gameId
                    );

                    if (Boolean(~index)) {
                      this.list[index].updatePeriodData(
                        data.periodData,
                        check,
                        checkFavorite
                      );
                    }
                  }
                }

                if (TOPIC === `sports.soccer.cheers`) {
                  index = copy.findIndex(
                    (item) => item.id === parseInt(data.game_id)
                  );
                  if (Boolean(~index)) {
                    this.list[index].cheerCount++;
                  }
                }

                // if (TOPIC === `sports.soccer.broadcasts`) {
                //   this.list[index].updateBroadcasts(data.special);
                // }
              }
            }
          }
        } catch (error) {
          console.log('=== socket on message error ===', error);
        }
      }
    };
  }

  /**********************************************************
   * computed
   **********************************************************/
  @computed
  get soccerList() {
    const { searchValue, selectedTab } = this;
    const list = this.list.toJS();
    const todayFinalList = this.todayFinalList.toJS();
    let filterList = [];

    if (searchValue.hasOwnProperty('id')) {
      let searchList = list;

      if (selectedTab === 'FINAL') {
        searchList = todayFinalList;
      } else {
        searchList = list;
      }

      filterList = searchList.filter((item) => {
        if (!!searchValue.isTeam) {
          return (
            item.teams.home.id === searchValue.id ||
            item.teams.away.id === searchValue.id
          );
        } else {
          return item.league.id === searchValue.id;
        }
      });
    } else if (selectedTab === 'ALL') {
      filterList = list;
    } else if (selectedTab === 'FINAL') {
      filterList = todayFinalList;
    } else {
      filterList = list.filter((item) => {
        if (selectedTab === 'FAVORITE') {
          return this.selectedGames.some((id) => item.id === id.key);
        }
      });
    }

    let deleteSelectedGames = [];

    if (this.selectedGames.length > 0) {
      deleteSelectedGames = filterList.filter((item) =>
        this.selectedGames.every((id) => item.id !== id.key)
      );
    } else {
      deleteSelectedGames = filterList;
    }

    const progressList = deleteSelectedGames.filter(
      (item) => _SPORTS_STATUS[item.gameStatus] !== 3
    );

    const finishedList = deleteSelectedGames.filter(
      (item) => _SPORTS_STATUS[item.gameStatus] === 3
    );

    let result = [];
    let finishedResult = [];

    result = this.unShiftHeader(progressList, result);
    finishedResult = this.unShiftHeader(finishedList, finishedResult);

    return { list: result, finishedList: finishedResult };
  }

  @computed
  get selectedList() {
    const { searchValue, selectedTab } = this;
    const list = this.list.toJS();
    const todaySelectedGames = this.todaySelectedGames.toJS();
    const todayFinalList = this.todayFinalList.toJS();
    const today = timeUtils.todayYmd();

    let selectedGameList = [];
    let filterList = [];

    if (searchValue.hasOwnProperty('id')) {
      filterList = [];
    } else if (selectedTab === 'FINAL') {
      filterList = todayFinalList;
    } else {
      filterList = list;
    }

    if (selectedTab === 'FAVORITE' && this.currentDate === today) {
      selectedGameList = todaySelectedGames;
    } else {
      if (this.selectedGames.length > 0) {
        selectedGameList = filterList.filter((item) =>
          this.selectedGames.some((id) => id.key === item.id)
        );
      }
    }

    return ScoreUtil.sortSportsList(selectedGameList);
  }

  @computed
  get isFavoriteActiveCheck() {
    const { selectedList } = this;

    return selectedList.length > 0;
  }

  /**
   * @description 축구 리스트 상단에 header 추가
   * @param {*} origin
   * @param {*} arr
   * @returns
   */
  unShiftHeader(origin, arr) {
    let temp = arr;

    if (origin.length === 0) {
      return arr;
    } else {
      let checkIdx = 0;

      origin.every((item, idx) => {
        if (!!item.startDatetime) {
          const nextId = idx + 1 >= origin.length ? idx : idx + 1;
          const dateLeft = item.startDatetime;
          const dateRight = origin[nextId].startDatetime;

          const checkDate = differenceInCalendarDays(
            new Date(dateLeft.split('T')[0]),
            new Date(dateRight.split('T')[0])
          );

          if (checkDate === -1) {
            checkIdx = idx;
            return false;
          } else {
            return true;
          }
        }
      });

      if (checkIdx === 0) {
        temp.push({
          isHeader: true,
          isFinished: _SPORTS_STATUS[origin[0].gameStatus] === 3,
          date: timeUtils.sportsDateFormatIos(origin[0].startDatetime),
          id: origin[0].startDatetime
        });
        temp = [
          ...temp,
          ...ScoreUtil.sortSoccerList(origin, this.selectedGames)
        ];

        return this.unShiftHeader([], temp);
      } else {
        const pushArr = origin.slice(0, checkIdx + 1);
        const deleteArr = origin.slice(checkIdx + 1, origin.length);

        temp.push({
          isHeader: _SPORTS_STATUS[origin[0].gameStatus] === 3 ? false : true,
          isFinished: _SPORTS_STATUS[origin[0].gameStatus] === 3,
          date: timeUtils.sportsDateFormatIos(origin[0].startDatetime),
          id: origin[0].startDatetime
        });
        temp = [
          ...temp,
          ...ScoreUtil.sortSoccerList(pushArr, this.selectedGames)
        ];

        return this.unShiftHeader(deleteArr, temp);
      }
    }
  }

  @computed
  get soccerCount() {
    const { currentDate } = this;
    const today = timeUtils.todayYmd();
    const list = this.list.toJS();
    const selectedList = this.selectedGames.toJS();
    let filterSelectedDate = [];

    if (selectedList.length > 0) {
      if (currentDate === today) {
        filterSelectedDate = selectedList.filter((item) => {
          const date = item.date.split('T')[0];

          return (
            date === currentDate ||
            date === timeUtils.datePrevOrNext(currentDate, 'next')
          );
        });
      } else {
        filterSelectedDate = selectedList.filter((item) => {
          const date = item.date.split('T')[0];
          return date === currentDate;
        });
      }
    }

    return {
      all: list.length,
      favorite: filterSelectedDate.filter(
        (item) => item.sportsType === 'soccer'
      ).length
    };
  }

  @computed
  get getFavoriteTodayGameIds() {
    const { currentDate } = this;
    const today = timeUtils.todayYmd();
    const selectedList = this.selectedGames.toJS();
    let filterSelectedDate = [];
    let result = [];

    if (selectedList.length > 0) {
      if (currentDate === today) {
        filterSelectedDate = selectedList.filter((item) => {
          const date = item.date.split('T')[0];

          return (
            date === currentDate ||
            date === timeUtils.datePrevOrNext(currentDate, 'next')
          );
        });
      }
    }

    if (filterSelectedDate.length > 0) {
      result = filterSelectedDate.map((item) => {
        return item.key;
      });
    }

    return result.join(',');
  }

  /**********************************************************
   * actions
   **********************************************************/
  @action
  closeSocket() {
    this.socket.close(() => {
      runInAction(() => {
        this.isSub = false;
        this.socket = null;
      });
    });
  }

  @action
  setSearchValue(data) {
    this.searchValue = data;
  }

  @action
  clearSearchValue() {
    this.searchValue = {};
  }

  @action
  setSoccerSelectedTab(tab) {
    this.selectedTab = tab;
  }

  @action
  setSuggestList(date) {
    const today = timeUtils.todayYmd();
    let list = this.list.toJS();

    if (date === today) {
      if (this.selectedTab === 'ALL') {
        this.setSuggestData(list);
      } else if (this.selectedTab === 'FINAL') {
        this.setSuggestData(this.todayFinalList.toJS());
      }
    } else {
      this.setSuggestData(list);
    }
  }

  @action
  setSuggestData(list) {
    const leagues = [
      ...new Map(
        list.map((item) => [
          item.league.id,
          { name: item.league.shortName, id: item.league.id, isTeam: false }
        ])
      ).values()
    ];

    let teamsArr = [];
    let teams = [];

    list.forEach((item) => {
      if (!!item.teams.home) {
        teamsArr.push({
          name: item.teams.home.name,
          id: item.teams.home.id,
          isTeam: true
        });
      }
      if (!!item.teams.away) {
        teamsArr.push({
          name: item.teams.away.name,
          id: item.teams.away.id,
          isTeam: true
        });
      }
    });

    if (teamsArr.length > 0) {
      teams = [...new Map(teamsArr.map((item) => [item.id, item])).values()];
    }

    this.suggestList = [
      {
        title: '리그',
        list: leagues
      },
      {
        title: '팀',
        list: teams
      }
    ];
  }

  /**********************************************************
   * async actions
   **********************************************************/
  @asyncAction
  *getSoccerToday() {
    this.loading = true;

    try {
      this.currentDate = timeUtils.todayYmd();

      if (!this.isSub) {
        yield this.connectSocket([
          'sports.soccer.games',
          'sports.soccer.period-data',
          'sports.soccer.cheers'
          // 'sports.soccer.broadcasts'
        ]);
      }

      const result = yield ScoreService.getSportsSoccerTodayGames();

      if (result) {
        this.list = result.map((item) => new SoccerModel(item));

        this.loading = false;
      }
    } catch (error) {
      console.log(error);
    }
  }

  @asyncAction
  *getTodayFinalCount() {
    try {
      const todayFinalCount = yield ScoreService.getSoccerTodayFinalCount();

      if (todayFinalCount) {
        runInAction(() => {
          this.todayFinalCount = todayFinalCount[0].gameCount;
        });
      }
    } catch (error) {}
  }

  @asyncAction
  *getSoccer(date) {
    this.loading = true;

    try {
      this.currentDate = date;

      if (!this.isSub) {
        yield this.connectSocket([
          'sports.soccer.games',
          'sports.soccer.period-data',
          'sports.soccer.cheers'
          // 'sports.soccer.broadcasts'
        ]);
      }

      const result = yield ScoreService.getSportsTypeGames(
        date,
        'ALL',
        'soccer'
      );

      if (result) {
        this.list = result.map((item) => new SoccerModel(item));

        this.loading = false;
      }
    } catch (error) {
      console.log(error);
    }
  }

  @asyncAction
  *getTodayFinalSoccer(date) {
    this.loading = true;

    try {
      this.currentDate = timeUtils.todayYmd();

      const result = yield ScoreService.getSportsTypeGames(
        date,
        'FINAL',
        'soccer'
      );

      if (result) {
        this.todayFinalList = result.map((item) => new SoccerModel(item));

        this.loading = false;
      }
    } catch (error) {
      console.log(error);
    }
  }

  @asyncAction
  *getSoccerInterestGames(gameIds) {
    this.loading = true;

    try {
      const result = yield ScoreService.getSoccerInterestGames(gameIds);

      if (result) {
        this.todaySelectedGames = result.map((item) => new SoccerModel(item));

        this.loading = false;
      }
    } catch (error) {
      console.log(error);
    }
  }
}

export default SoccerStore;
